import { ChangeDetectionStrategy, Component, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import { ResearchContextService } from '../../../../core/research/research-context.service';
import { Research } from '../../../../core/api/research-api.service';

/**
 * Hallmark · component: research selector · genre: modern-minimal
 * states: default · hover · focus-visible · active(open) · disabled ·
 *         loading · error · empty(no researches)
 * contrast: pass
 *
 * Sits at the left of the topbar. Shows the active research's short name and
 * switches the workspace context; per-user and persisted server-side.
 */
@Component({
  selector: 'app-research-selector',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
  styles: [`
    .rs-pop { display: block; position: absolute; top: calc(100% + 8px); left: 0; z-index: 70;
      min-width: 300px; max-width: min(92vw, 380px);
      box-shadow: 0 12px 40px rgba(0,0,0,0.6); }
    .rs-pop.closed { display: none; }
  `],
  template: `
    <div class="relative">
      <button
        type="button"
        (click)="toggle()"
        [attr.aria-expanded]="open()"
        [disabled]="ctx.loading() && !ctx.researches().length"
        class="group flex items-center gap-2 rounded-lg border border-[var(--color-surface-border)] bg-[var(--color-surface-raised)] px-2.5 py-1.5 text-left transition-colors hover:border-[var(--color-brand-600)]/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-brand-500)] disabled:opacity-60">
        @if (ctx.loading() && !ctx.researches().length) {
          <span class="h-2 w-2 rounded-full bg-[var(--color-text-faint)] animate-pulse"></span>
          <span class="text-xs text-[var(--color-text-muted)]">Memuat…</span>
        } @else if (ctx.active(); as r) {
          <span class="inline-flex h-5 items-center rounded px-1.5 text-[9px] font-bold tracking-wide"
                [class]="typeBadge(r)">{{ typeShort(r) }}</span>
          <span class="hidden sm:block max-w-[140px] truncate text-xs font-semibold text-[var(--color-text-primary)]"
                [title]="r.name">{{ r.short_name || r.name }}</span>
          <i class="fa-solid fa-chevron-down text-[9px] text-[var(--color-text-faint)] transition-transform"
             [class.rotate-180]="open()"></i>
        } @else {
          <i class="fa-solid fa-circle-exclamation text-[var(--color-warning)] text-xs"></i>
          <span class="text-xs text-[var(--color-text-muted)]">Tidak ada penelitian</span>
        }
      </button>

      <div class="rs-pop rounded-xl border border-[var(--color-surface-border)] bg-[var(--color-surface-raised)] overflow-hidden"
           [class.closed]="!open()">
        <div class="px-3 py-2 border-b border-[var(--color-surface-border)]">
          <p class="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">Pilih Penelitian</p>
        </div>
        <div class="max-h-80 overflow-y-auto">
          @if (!ctx.researches().length) {
            <p class="px-3 py-6 text-center text-xs text-[var(--color-text-muted)]">Belum ada penelitian terdaftar.</p>
          } @else {
            @for (r of ctx.researches(); track r.id) {
              <button
                type="button"
                (click)="select(r)"
                class="flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-[var(--color-surface-hover)]"
                [class]="isActive(r) ? 'bg-[var(--color-brand-500)]/10' : ''">
                <span class="mt-0.5 inline-flex h-4 items-center rounded px-1.5 text-[9px] font-bold"
                      [class]="typeBadge(r)">{{ typeShort(r) }}</span>
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-xs font-semibold text-[var(--color-text-primary)]">{{ r.short_name || r.name }}</span>
                  <span class="mt-0.5 block truncate text-[10px] text-[var(--color-text-muted)]">{{ r.name }}</span>
                  <span class="mt-0.5 block text-[10px] text-[var(--color-text-faint)]">
                    {{ r.lead || (r.researchers?.join(', ') || '—') }}{{ r.year ? ' · ' + r.year : '' }}
                  </span>
                </span>
                @if (isActive(r)) {
                  <i class="fa-solid fa-check mt-0.5 text-[var(--color-brand-400)] text-xs shrink-0"></i>
                }
              </button>
            }
          }
        </div>
        <a routerLink="/researches" (click)="open.set(false)"
           class="flex items-center gap-2 border-t border-[var(--color-surface-border)] px-3 py-2.5 text-xs text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text-primary)]">
          <i class="fa-solid fa-sliders text-[10px]"></i>Kelola penelitian
          <i class="fa-solid fa-arrow-right ml-auto text-[10px] text-[var(--color-text-faint)]"></i>
        </a>
      </div>
    </div>
  `,
})
export class ResearchSelectorComponent implements OnInit {
  readonly ctx = inject(ResearchContextService);
  readonly open = signal(false);

  ngOnInit(): void {
    this.ctx.ensureLoaded();
  }

  toggle(): void {
    this.open.update(v => !v);
  }

  select(r: Research): void {
    this.ctx.setActive(r.id);
    this.open.set(false);
  }

  isActive(r: Research): boolean {
    return this.ctx.active()?.id === r.id;
  }

  typeShort(r: Research): string {
    if (r.type === 'dissertation') return 'DIS';
    if (r.type === 'replication') return 'REP';
    return 'OTH';
  }

  typeBadge(r: Research): string {
    if (r.type === 'dissertation') return 'bg-[var(--color-brand-500)]/15 text-[var(--color-brand-300)] border border-[var(--color-brand-600)]/40';
    if (r.type === 'replication') return 'bg-[var(--color-primary-500)]/15 text-[var(--color-primary-300)] border border-[var(--color-primary-600)]/40';
    return 'bg-[var(--color-surface-hover)] text-[var(--color-text-muted)] border border-[var(--color-surface-border)]';
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    const el = e.target as HTMLElement;
    if (!el.closest('app-research-selector')) this.open.set(false);
  }
}
